import Constants from 'expo-constants';
import type { ImageSourcePropType } from 'react-native';

type SplashEnv = 'development' | 'preview' | 'production' | 'showcase';

const SPLASH_ENVS: readonly SplashEnv[] = [
  'development',
  'preview',
  'production',
  'showcase',
];

/**
 * Per-env splash assets. Must mirror `getDynamicConfig` in `app.config.ts`, or
 * the JS overlay renders a different mark than the native splash it replaces.
 */
const SPLASH_ASSETS: Record<SplashEnv, ImageSourcePropType> = {
  development: require('@/assets/splash/Flavrly-App-Splash_dev.png'),
  preview: require('@/assets/splash/Flavrly-App-Splash_preview.png'),
  showcase: require('@/assets/splash/Flavrly-App-Splash_preview.png'),
  production: require('@/assets/splash/Flavrly-App-Splash_production.png'),
};

/**
 * Narrows an unknown build-time env value to one we have an asset for,
 * defaulting to production — shipping the production mark to an unrecognised
 * env is always safer than shipping a dev-branded one to real users.
 */
export function resolveSplashEnv(value: unknown): SplashEnv {
  return SPLASH_ENVS.includes(value as SplashEnv)
    ? (value as SplashEnv)
    : 'production';
}

/**
 * The splash image the native splash screen is currently showing.
 *
 * Reads `extra.appEnv` (set by `app.config.ts` from `APP_ENV` at build time),
 * NOT `ENV.APP_ENV` — that one reads `EXPO_PUBLIC_APP_ENV`, which only the web
 * build scripts set, so it reports "development" in native production builds.
 */
export function getSplashAsset(): ImageSourcePropType {
  const env = resolveSplashEnv(Constants.expoConfig?.extra?.appEnv);
  return SPLASH_ASSETS[env];
}

export type { SplashEnv };
